import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import Transform from "@mui/icons-material/Transform";
import FilterAlt from "@mui/icons-material/FilterAlt";
import EditNote from "@mui/icons-material/EditNote";
import { AgGridReact } from "ag-grid-react";
import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { isEmpty, isNil, size } from "lodash";

import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import CustomHeaderMenuIcon from "@plannel/components/aggrid/CustomHeaderMenuIcon";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import PaginationContainer from "@plannel/components/PaginationContainer";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import { FilterButton, ResetButton } from "@plannel/components/ActionIconButton";
import Snackbar from "@plannel/components/Snackbar";
import CustomerHrchyTreeFilter from "@plannel/components/filter/CustomerHrchyTreeFilter";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import { DPFilterModal } from "@plannel/pages/demand-plan/dp-workbench/dp-workbench-modal/FilterModal";
import DimensAttrModal from "@plannel/pages/demand-plan/dp-workbench/dp-workbench-modal/DimensAttrModal";
import DemandNettingNoteModal, { buildNoteKey } from "@plannel/pages/demand-plan/demand-management/DemandNettingNoteModal";
import DpVersionSelect from "@plannel/pages/demand-plan/dp-workbench/DpVersionSelect";

import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import codeUtil from "@plannel/pages/utils/code-util";
import demandNettingService from "@plannel/services/dp/demand-netting-service";
import companyService from "@plannel/services/data/company-service";
import p13nService from "@plannel/services/system/p13n-service";

const { snackBarMessageColor } = ColorUtil;
const { customer, item, filterType, muicolor } = codeUtil;

/** 모달이 준 [논리, 연산자] 쌍을 고급 필터 노드의 값으로 바꾼다 (DP 워크벤치와 같은 규칙). */
const operatorOf = arr => (arr?.[1] === filterType.notEqual?.cd ? "notEqual" : "equals");
const logicOf = arr => (arr?.[0] === filterType.and?.cd ? "AND" : "OR");

/** 필터 모달의 속성 키 → 서버 바인딩 키 (DpColumn.DemandNetting 에 등록된 이름). */
const BIND_NAME = {
  customer: { name: "customerName", code: "customerCd" },
  item: { name: "itemName", code: "itemCd" }
};

const MODE_NETTING = "NETTING";
const GRID_ID = "DEMAND_NETTING_GRID";
/** 개인화(z_p13n) 키. 차원 설정이 여기 저장된다. */
const PAGE_CD = "DP_DEMAND_NETTING";
const PAGE_SIZES = [20, 50, 100, 200, 500];

/**
 * 고급 필터에 노출할 컬럼. DP 워크벤치와 같은 구성이되 이 화면에 없는 축은 뺐다.
 *
 * ⚠ headerName 은 <b>화면 라벨</b>(i18n 키)이고, 서버 바인딩 키는 filterName 이다.
 *   컴포넌트가 `value={col.filterName || col.headerName}` 으로 고른다. 반대로 넣으면
 *   목록에 바인딩 키가 그대로 보이고, 서버에서는 enum 에 없어 조건이 조용히 사라진다.
 *
 * ⚠ groupLabel 은 <b>바로 앞 컬럼과 비교</b>해 찍힌다. 그래서 축이 바뀌는 자리에는
 *   반드시 groupLabel 이 있어야 한다 - 없으면 앞 그룹 밑으로 딸려 들어간다.
 *
 * @param attrConfig 사용자 지정 속성명 { item: {attr01: "라벨"}, customer: {...} }.
 *   이름이 없으면 attr01 처럼 원문이 보인다 (워크벤치와 같은 처리).
 */
const buildFilterColumnDefs = (t, attrConfig) => {
  const attrColumns = (scope, groupLabel) =>
    Array.from({ length: 10 }, (_, idx) => {
      const order = idx + 1;
      const attrCd = `attr${String(order).padStart(2, "0")}`;
      return {
        headerName: attrConfig?.[scope]?.[attrCd] ?? attrCd,
        filterName: `${scope}Attr${order}`,
        groupLabel,
        filterType: "string"
      };
    });

  const basic = (headerName, groupLabel) => ({ headerName, filterName: headerName, groupLabel, filterType: "string" });

  const itemGroup = t("item");
  const custGroup = t("customer");

  return [
    basic("itemCd", itemGroup),
    basic("itemName", itemGroup),
    basic("itemHrchyCd", itemGroup),
    basic("itemHrchyNm", itemGroup),
    basic("itemType", itemGroup),
    basic("invClassCd", itemGroup),
    basic("uomCd", itemGroup),
    ...attrColumns("item", `${itemGroup} ${t("attr")}`),

    /*
     * 기준일자. 이 화면의 페이징 단위는 조합이라 조합 목록에는 날짜가 없다 —
     * 서버가 이 조건을 조회 기간(startDate · endDate)으로 바꿔 적용한다.
     */
    { headerName: "baseDt", filterName: "baseDate", groupLabel: t("time"), filterType: "date", filterUnit: "month" },

    basic("customerCd", custGroup),
    basic("customerName", custGroup),
    basic("customerHrchyCd", custGroup),
    basic("customerHrchyName", custGroup),
    basic("currencyCd", custGroup),
    ...attrColumns("customer", `${custGroup} ${t("attr")}`),

    /*
     * 제외 조건. 실제 DB 컬럼이 아니라 서버가 코드를 보고 SQL 을 만든다
     * (DemandNettingSearchDto.translateExclusionToRawSql). RpReview 의 excludeCondition 과 같은 방식.
     */
    {
      headerName: "excludeCondition",
      filterName: "excludeCondition",
      groupLabel: t("dnScope"),
      filterType: "select",
      filterOption: ["emptySalesOrder", "emptyRemainFcst"]
    }
  ];
};

/** onApply 페이로드가 트리 / 래핑 두 형태로 와서 정규화한다 (AdvancedFilter 스킬 §2-2). */
const filterTreeOf = filters => filters?.advancedFilter ?? filters?.filter ?? filters ?? null;
const hasFilterConditions = filters => (filterTreeOf(filters)?.children?.length ?? 0) > 0;

/**
 * 스냅샷의 EXECUTED_TS 를 읽기 좋게 자른다.
 *
 * 서버가 LocalDateTime.toString() 으로 넣으므로 "2026-09-11T11:33:25.135525" 처럼
 * 오프셋이 없다. new Date() 에 넣으면 브라우저가 UTC 로 보거나 로컬로 보거나 해서
 * 시각이 밀린다. 이미 서버 로컬 시각이므로 문자열을 그대로 자른다.
 */
const formatExecutedTs = ts => {
  if (isNil(ts) || ts === "") return "-";
  const matched = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})/.exec(ts);
  return matched ? `${matched[1]} ${matched[2]}` : ts;
};

/** 차감 범위가 [전 구간]일 때 서버가 쓰는 그룹 라벨 (DemandNettingEngine.GROUP_ALL). */
const GROUP_ALL = "ALL";

/**
 * 결과 행. 저장된 것은 일부뿐이고 나머지는 파생이다.
 *
 * 코드 기준으로 행을 만든다 — 주문 타입이 7종으로 늘어도 이 배열만 늘리면 되고
 * 화면을 다시 짜지 않는다 (설계서 요구).
 */
const ROW_DEFS = [
  { key: "SO", label: "dnRowSalesOrder", pick: c => c.soQty },
  { key: "REMAIN_FCST", label: "dnRowRemainFcst", pick: c => c.remainQty },
  { key: "MF", label: "dnRowForecast", pick: c => c.mfQty },
  // 수요 차감 수량 = 원본 예측 - 잔여 예측
  { key: "DEDUCTED", label: "dnRowDeducted", pick: c => derive(c.mfQty, c.remainQty) },
  /*
   * 예측 초과 SO 는 그룹 단위 값이라 그룹의 첫 버켓에만 들어 있다 (서버가 그렇게 채운다).
   *
   * 차감 범위가 [전 구간]이면 그룹이 지평 전체다 — 그때는 버켓 칸이 아니라 Total 열에 놓는다.
   * 버켓 칸에 있는 한 무슨 이름을 붙여도 그 버켓의 값으로 읽힌다(2026-09-28 사용자 지적).
   */
  { key: "EXCESS", label: "dnRowExcess", pick: c => c.excessQty, groupLevel: true,
    wholeRange: c => c.excessGroup === GROUP_ALL },
  /*
   * ⚠ RTF 5행(정시 · 지연 · 지연 도착 예정 · 지연 잔량 · 못 댐)은 2026-09-29 에 화면에서 내렸다.
   *
   * 기능을 버린 게 아니다 — 회신은 여전히 계산·저장된다(`z_plan_demand` 의 on_time/delay/short,
   * `z_mp_demand` 의 on_time_supply_qty). 조회 쿼리도 `rtfOnTimeQty` 등을 그대로 내려준다.
   * 화면에 다시 올리려면 이 자리에 행 정의만 되돌리면 된다.
   *
   * 내린 이유와 남은 설계 과제는 `saas-application/docs/demand-netting/09-rtf-capacity-loop.md`.
   * 요지는 지금 RTF 가 RP 의 답인데 RP 는 공장 능력을 안 본다는 것이다 — MP 가 못 만들어도
   * 화면은 낙관적으로 답한다. 그 구멍을 메우기 전에는 영업에게 보일 값이 아니다.
   *
   * 되돌릴 때 같이 살릴 것 — 행 정의와 함께 지운 헬퍼 둘이 커밋에 남아 있다 :
   *   `rtfTip`        출처(RP|MP) · 도착일 툴팁 재료
   *   `delayBacklog`  지연 잔량 (버켓을 걸으며 도착분을 털고 새 지연을 얹는다)
   * `git show 148f7583b5 cfb339de02 -- <이 파일>` 로 꺼내면 된다.
   *
   * 그리고 `reply` 플래그가 하는 일을 잊지 말 것 — 회신 없음을 빈칸이 아니라 "-" 로 찍는
   * 규칙이다. 빈칸으로 두면 "공급 0" 으로 읽힌다.
   */
];

const derive = (mf, remain) => {
  if (isNil(mf) && isNil(remain)) return null;
  return (mf ?? 0) - (remain ?? 0);
};

const fmtQty = v =>
  isNil(v) || v === "" ? "" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });

/**
 * Demand Netting — 실행 + 결과 조회.
 *
 * DP 안의 한 기능이지 독립 모듈이 아니다. 예측 편집은 기존 Workbench 화면에 그대로 있고,
 * 여기서는 네팅을 돌리고 결과만 본다.
 */
const DemandNetting = props => {
  const { t, viewName } = props;

  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const gridRef = useRef(null);
  // 상단 범위·순서를 이 버전의 실행 옵션으로 맞춘 적이 있는가 (syncOptionToVersion 참조)
  const optionSyncedVerRef = useRef(null);

  const [config, setConfig] = useState(null);
  const [selectVersion, setSelectVersion] = useState(reduxViewState?.selectVersion ?? null);
  const [vListReload, setVListReload] = useState(false);

  const [scope, setScope] = useState("ALL");
  const [method, setMethod] = useState("EARLIEST");

  // searchValue 는 자동완성이 입력 텍스트를 담는 ref 다(내부에서 .current 를 읽는다).
  // 사용자가 고른 객체는 setSelectedXxx 콜백으로 따로 받는다 - 둘은 다른 것이다.

  const [buckets, setBuckets] = useState([]);
  const [cells, setCells] = useState([]);
  const [executedOption, setExecutedOption] = useState(null);

  // 페이징 - 단위는 행이 아니라 품목 × 거래처 조합이다. 한 조합이 5행을 만든다
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? PAGE_SIZES[0]);
  const [totalRows, setTotalRows] = useState(0);

  // 고급 필터 - 상단 필터와 병행하지 않는 단독 모드 (AdvancedFilter 스킬 §4-3)
  // 계층 트리 필터 - customerHrchys / category 두 키로 들어온다 (BfLeaderboard 와 같은 계약)
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? {});
  const [custHrchyDefault] = useState(reduxViewState?.filter?.customerHrchys ?? []);
  const [itemHrchyDefault] = useState(reduxViewState?.filter?.category ?? []);

  // 차원 순서 - true 면 품목 → 거래처. DP 워크벤치의 orderByItem 과 같은 뜻이다
  const [orderByItem, setOrderByItem] = useState(reduxViewState?.orderByItem ?? true);

  // 코드 목록 필터(깔때기 아이콘). 모달이 { modelCode, filterList, filterType } 를 준다
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [modelCode, setModelCode] = useState(customer.name);
  const [filterData, setFilterData] = useState({});
  const [customerFilterList, setCustomerFilterList] = useState(reduxViewState?.customerFilterList ?? {});
  const [itemFilterList, setItemFilterList] = useState(reduxViewState?.itemFilterList ?? {});
  const [custFilterType, setCustFilterType] = useState(reduxViewState?.custFilterType ?? [filterType.or.cd, filterType.equal.cd]);
  const [itemFilterType, setItemFilterType] = useState(reduxViewState?.itemFilterType ?? [filterType.or.cd, filterType.equal.cd]);

  // 사용자 지정 속성명. 고급 필터의 attr1..10 라벨에 쓴다
  const [attrConfig, setAttrConfig] = useState(null);
  /*
   * 차원 설정 - 어떤 속성 컬럼을 띄울지. { attr: ["attr01",...], custAttr: [...] }
   * DimensAttrModal 이 p13n(z_p13n) 에 직접 저장하므로 화면은 읽어서 넘겨주기만 한다.
   */
  const [dimensionAttrData, setDimensionAttrData] = useState(null);
  const [dimenModalOpen, setDimenModalOpen] = useState(false);
  // 메모. 조합(품목 × 거래처)마다 하나이고 z_dp_note 에 Netting_ 접두 키로 저장된다
  const [noteOpen, setNoteOpen] = useState(false);
  // 노트 패널이 볼 조합. 그리드에서 행을 고르면 여기 담긴다
  // 노트 대상 셀 — 버켓 칸을 눌러야 정해진다(거래처 · 품목 · 버켓 · 지표)
  const [selectedCell, setSelectedCell] = useState(null);
  // 노트가 달린 조합 키. 그리드에 표시만 하므로 Set 하나면 된다
  const [notedKeys, setNotedKeys] = useState(new Set());
  const [dimenAttrTarget, setDimenAttrTarget] = useState("item");
  /*
   * 차원 설정에서 고른 계층 그룹. hasLevel 모드의 저장 구조가
   * dimensionAttrData[item|customer][grpCd] 라서 어느 탭을 볼지 기억해야 한다.
   */
  const [itemHrchyGrp, setItemHrchyGrp] = useState(reduxViewState?.itemHrchyGrp ?? null);
  const [custHrchyGrp, setCustHrchyGrp] = useState(reduxViewState?.custHrchyGrp ?? null);
  // 리셋 때 트리 필터를 다시 읽히는 신호 (redux 를 비운 뒤 올려야 되살아나지 않는다)
  const [treeload, setTreeload] = useState(true);
  const [itemTreeload, setItemTreeload] = useState(true);

  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ?? null);
  const [running, setRunning] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, content: "", severity: "info" });

  const notify = useCallback((content, severity = "info") => setSnackInfo({ open: true, content, severity }), []);

  const isNettingMode = config?.mode === MODE_NETTING;
  const enabled = config?.enabled === true;

  /**
   * [버켓 내] 는 그룹에 버켓이 하나라 방식 3종이 같은 결과를 낸다.
   * 무의미한 선택지를 열어 두면 사용자가 바꿔 보고 "안 먹는다" 고 여긴다.
   */
  const methodDisabled = scope === "BUCKET";

  useEffect(() => {
    p13nService.get(PAGE_CD)
      .then(({ data }) => setDimensionAttrData(data?.ATTRIBUTE ?? {}))
      .catch(e => { debug(e); setDimensionAttrData({}); });
  }, []);

  useEffect(() => {
    companyService.getCustomConfig()
      .then(({ data }) => setAttrConfig(data ?? {}))
      .catch(e => { debug(e); setAttrConfig({}); });
  }, []);

  useEffect(() => {
    demandNettingService.getConfig()
      .then(({ data }) => {
        setConfig(data);
        // 버전의 실행 옵션이 이미 반영됐으면 테넌트 기본값으로 덮지 않는다.
        // 두 요청이 병렬이라 이 응답이 늦게 오면 사용자가 보는 값이 되돌아간다.
        if (optionSyncedVerRef.current === null) {
          setScope(data?.scope ?? "ALL");
          setMethod(data?.method ?? "EARLIEST");
        }
      })
      .catch(e => {
        debug(e);
        notify(t("MSG_RetrieveFailed"), "error");
      });
  }, [t, notify]);

  // 차원 설정에서 지금 보고 있는 탭. 컬럼과 행이 같은 값을 봐야 한다
  const dimTabs = useMemo(() => {
    const pick = (scope, grp) => {
      const byGrp = dimensionAttrData?.[scope];
      if (!byGrp) return null;
      return (grp ? byGrp[grp] : null) ?? byGrp[Object.keys(byGrp)[0]] ?? null;
    };
    return { item: pick("item", itemHrchyGrp), customer: pick("customer", custHrchyGrp) };
  }, [dimensionAttrData, itemHrchyGrp, custHrchyGrp]);

  /*
   * 이 버전의 노트 키를 통째로 읽는다.
   *   노트가 셀 단위라 키를 모아 물으면 페이지당 천 개가 넘는다. 버전 하나의 키는
   *   많아야 수백 개라 통째로 받아 화면이 대조하는 편이 싸다.
   */
  const loadNotedKeys = useCallback(() => {
    if (isNil(selectVersion?.id)) { setNotedKeys(new Set()); return; }
    demandNettingService.getNotedKeys({ planVerId: selectVersion.id })
      .then(({ data, status }) => {
        setNotedKeys(new Set(status === 204 || isNil(data) ? [] : (data.noted ?? [])));
      })
      .catch(e => { debug(e); setNotedKeys(new Set()); });
  }, [selectVersion]);

  /*
   * 상단의 범위 · 순서를 그 버전이 마지막으로 돌린 옵션으로 맞춘다.
   *
   * 화면에 보이는 결과는 그 옵션으로 나온 것이므로, 컨트롤이 다른 값을 가리키고 있으면
   * 표가 그 설정으로 계산된 것처럼 읽힌다(2026-09-28 사용자 지적). 돌린 적 없는 버전이면
   * 테넌트 기본값으로 되돌린다.
   *
   * 버전이 바뀔 때 한 번만 맞춘다 — 조회가 돌 때마다 맞추면 사용자가 방금 고른 값을
   * 페이지 이동이나 필터 변경이 덮어쓴다.
   */
  const syncOptionToVersion = useCallback(executed => {
    const verId = selectVersion?.id ?? null;
    if (optionSyncedVerRef.current === verId) return;
    optionSyncedVerRef.current = verId;
    setScope(executed?.SCOPE ?? config?.scope ?? "ALL");
    setMethod(executed?.METHOD ?? config?.method ?? "EARLIEST");
  }, [selectVersion, config]);

  const retrieve = useCallback(() => {
    if (isNil(selectVersion?.id)) return;

    const params = {
      planVerId: selectVersion.id,
      page: currentPage,
      pageSize,
      // 계층 코드를 그대로 보낸다. 말단 코드로 펼치는 건 서버(SearchService) 몫이다
      orderByItem,
      itemFilter: filterValue?.category?.map(node => node?.hrchyCd) ?? null,
      customerFilter: filterValue?.customerHrchys?.map(node => node?.hrchyCd) ?? null
    };
    /*
     * 코드 목록 필터는 서버에 새 파라미터를 만들지 않고 고급 필터 조건으로 접는다.
     *   DpColumn.DemandNetting 에 itemCd · itemName · customerCd · customerName 이
     *   이미 등록돼 있어 그대로 WHERE 가 된다. 워크벤치도 같은 방식이다.
     */
    const children = [];
    const userTree = filterTreeOf(advancedFilters);
    if (userTree?.children?.length > 0) {
      children.push(userTree);
    }
    const codeConditions = (list, scope, typeArr) => {
      const op = operatorOf(typeArr);
      const leaves = Object.entries(list ?? {}).flatMap(([key, values]) =>
        (values ?? []).map(val => ({
          filterType: "string",
          headerName: key === "name" ? BIND_NAME[scope].name : BIND_NAME[scope].code,
          operator: op,
          values: [val]
        })));
      if (leaves.length === 0) return;
      children.push(leaves.length === 1 ? leaves[0] : { logicOperator: logicOf(typeArr), children: leaves });
    };
    codeConditions(customerFilterList, "customer", custFilterType);
    codeConditions(itemFilterList, "item", itemFilterType);

    // 조건이 없으면 아예 안 보낸다 - 빈 트리를 보내면 서버가 WHERE 를 만들다 만다
    if (children.length > 0) {
      params.advancedFilters = { logicOperator: "AND", children };
    }

    demandNettingService.getResult(params)
      .then(({ data, status }) => {
        if (status === 204 || isNil(data)) {
          setCells([]);
          setBuckets([]);
          setTotalRows(0);
          // 실행 이력도 같이 비운다. 안 그러면 결과 없는 버전으로 옮겨도 이전 값이 남는다.
          setExecutedOption(null);
          syncOptionToVersion(null);
          return;
        }
        setExecutedOption(data.executedOption ?? null);
        syncOptionToVersion(data.executedOption ?? null);
        setCells(data.rows ?? []);
        setBuckets(collectBuckets(data.rows ?? []));
        setTotalRows(data.totalRows ?? 0);
      })
      .catch(e => {
        debug(e);
        notify(t("MSG_RetrieveFailed"), "error");
      });
  }, [t, notify, selectVersion, currentPage, pageSize, advancedFilters, filterValue,
    orderByItem, customerFilterList, itemFilterList, custFilterType, itemFilterType,
    syncOptionToVersion]);

  useEffect(() => { retrieve(); }, [retrieve]);

  /*
   * 노트 키는 버전에만 달렸다 - 조회 결과 안에서 불러오면 안 된다.
   * loadNotedKeys 가 매번 새 Set 을 넣으므로, retrieve 가 그걸 의존하면
   * 조회 → 새 Set → retrieve 새로 생성 → 조회 로 끝없이 돈다. 그 사이 rowData 가
   * 계속 갈려 세로 스크롤이 맨 위로 튕긴다.
   */
  useEffect(() => { loadNotedKeys(); }, [loadNotedKeys]);

  /*
   * 화면 행은 파생값이다. 차원 설정이나 노트 표식이 바뀌었다고 서버를 다시 부를 이유가 없다.
   */
  const rows = useMemo(() => buildRows(cells, dimTabs, notedKeys), [cells, dimTabs, notedKeys]);

  /*
   * 모달 결과를 목록에 담고, 같은 축의 계층 선택은 비운다.
   *   워크벤치와 같은 규칙 - 한 축에 조건이 걸리는 자리는 계층 트리 아니면 코드 목록 하나다.
   *   둘을 겹치면 왜 좁아졌는지 화면만 보고 알 수 없다.
   */
  useEffect(() => {
    if (!filterData.filterList) return;
    if (filterData.modelCode === customer.name) {
      setCustomerFilterList(filterData.filterList ?? {});
      setCustFilterType(filterData.filterType);
      setFilterValue(prev => ({ ...prev, customerHrchys: [], customerCheckSelected: [] }));
    } else {
      setItemFilterList(filterData.filterList ?? {});
      setItemFilterType(filterData.filterType);
      setFilterValue(prev => ({ ...prev, category: [] }));
    }
  }, [filterData]);

  /*
   * 조건이 바뀌면 1페이지로 돌아간다.
   *   5페이지를 보다가 조건을 좁히면 결과가 3페이지밖에 없을 수 있다 - 그러면 빈 화면이 뜨고
   *   사용자는 "필터를 걸었더니 아무것도 없다"고 읽는다.
   */
  useEffect(() => {
    setCurrentPage(1);
  }, [selectVersion, advancedFilters, filterValue, customerFilterList, itemFilterList]);

  // 조합 수 기준. 화면의 5행 × 조합 이 아니다
  const totalPages = Math.max(Math.ceil(totalRows / pageSize), 1);
  const advancedFilterColor = hasFilterConditions(advancedFilters) ? "info" : "default";

  /*
   * 조회 상태를 redux 에 남긴다 - 탭을 떠났다 돌아와도 보던 자리가 유지된다.
   *
   * filter(계층 트리) 는 여기서 안 쓴다. 트리 필터 컴포넌트가 redux 의 같은 슬롯을
   * 직접 읽고 쓰기 때문에(CustomerHrchyTreeFilter 의 createFilterViewState), 여기서
   * 같이 쓰면 둘이 서로를 덮는다.
   */
  useEffect(() => {
    const reduxState = reduxUtil.createObjectState(viewName, {
      selectVersion, currentPage, pageSize, advancedFilters, orderByItem,
      customerFilterList, itemFilterList, custFilterType, itemFilterType,
      itemHrchyGrp, custHrchyGrp
    });
    reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));
  }, [reduxDispatch, viewName, selectVersion, currentPage, pageSize, advancedFilters, orderByItem,
    customerFilterList, itemFilterList, custFilterType, itemFilterType, itemHrchyGrp, custHrchyGrp]);

  /*
   * 조회 조건을 전부 되돌린다. 버전은 그대로 둔다 - 보던 계획을 바꾸는 건 리셋이 아니다.
   *
   * ⚠ React state 만 비우면 안 된다. 계층 트리 필터는 값을 바꿀 때마다 redux 의
   *   filter 슬롯을 읽어 그 위에 덮어쓰므로, 비우지 않으면 트리가 다시 읽히면서
   *   남아 있던 customerHrchys 가 그대로 되살아난다 (워크벤치가 같은 문제를 겪었다).
   */
  const handleReset = () => {
    setFilterValue({});
    setCustomerFilterList({});
    setItemFilterList({});
    setCustFilterType([filterType.or.cd, filterType.equal.cd]);
    setItemFilterType([filterType.or.cd, filterType.equal.cd]);
    setAdvancedFilters(null);
    setOpenFilter(false);
    setCurrentPage(1);
    setPageSize(PAGE_SIZES[0]);
    setOrderByItem(true);

    if (reduxViewState) {
      reduxViewState.filter = {};
    }
    // 트리에게 "다시 읽어라" 를 알린다. redux 를 비운 뒤여야 빈 상태로 다시 선다
    setTreeload(prev => !prev);
    setItemTreeload(prev => !prev);
  };

  const runNetting = () => {
    if (isNil(selectVersion?.id)) return;
    setRunning(true);

    demandNettingService.execute({ planVerId: selectVersion.id, scope, method })
      .then(({ data }) => {
        notify(`${t("MSG_DemandNettingDone")} (${data?.savedCount ?? 0})`, "success");
        retrieve();
      })
      .catch(e => {
        debug(e);
        // 409 = 레거시 모드 또는 기능 꺼짐, 400 = 성립하지 않는 옵션 조합.
        // 서버가 이유를 문자열로 주므로 그대로 보여준다.
        const message = e?.response?.data?.message;
        notify(message ?? t("MSG_SaveFailed"), "error");
      })
      .finally(() => setRunning(false));
  };

  const openDimen = useCallback(target => { setDimenAttrTarget(target); setDimenModalOpen(true); }, []);

  /*
   * 노트 패널은 셀 선택과 무관하게 열고 닫는다 - DP 워크벤치와 같다.
   * 고른 칸이 없으면 패널이 빈 채로 뜨고, 칸을 누르면 그 자리에서 대상이 바뀐다.
   * 선택을 강요하면 "칸을 고르세요" 안내만 보고 무엇을 눌러야 하는지 알 수 없다.
   */
  const toggleNotePanel = useCallback(() => setNoteOpen(open => !open), []);



  /*
   * 모달이 저장한 뒤 그룹이 비어 있으면 저장된 첫 그룹을 따라간다.
   *   모달은 탭(계층 그룹)을 스스로 고르고 저장은 dimensionAttrData[축][그룹] 아래에 한다.
   *   화면이 그 그룹을 모르면 컬럼을 못 찾는다.
   */
  useEffect(() => {
    const first = scope => Object.keys(dimensionAttrData?.[scope] ?? {})[0] ?? null;
    if (!itemHrchyGrp && first("item")) setItemHrchyGrp(first("item"));
    if (!custHrchyGrp && first("customer")) setCustHrchyGrp(first("customer"));
  }, [dimensionAttrData, itemHrchyGrp, custHrchyGrp]);

  const columnDefs = useMemo(
    () => buildColumnDefs(t, buckets, orderByItem, dimTabs, attrConfig, openDimen),
    [t, buckets, orderByItem, dimTabs, attrConfig, openDimen]);
  const filterColumnDefs = useMemo(() => buildFilterColumnDefs(t, attrConfig), [t, attrConfig]);
  const defaultGridMemo = useMemo(
    () => DefaultGridSetting({ viewName, title: t("menuDpDemandNetting"), gridId: GRID_ID }),
    [t, viewName]);

  /*
   * 그리드에 넘기는 컬럼 관련 값은 반드시 같은 객체여야 한다. JSX 안에서 만들면 매 렌더마다
   * 새 배열/객체가 되고, ag-grid 는 그걸 "컬럼이 바뀌었다"로 보고 컬럼을 다시 만든다.
   * 그때 세로 스크롤 위치가 버려져, 훑어 내려가다 툴팁 하나만 떠도 맨 위로 튄다.
   */
  const gridColumnDefs = useMemo(() => GridUtils.getColumnDefs(columnDefs), [columnDefs]);

  /* 이 화면은 조회 전용이다 - DefaultGridSetting 의 editable:true 를 덮는다 */
  const readOnlyColDef = useMemo(
    () => ({ ...defaultGridMemo.defaultColDef, editable: false }), [defaultGridMemo]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/*
        hasIcons 를 끄지 않는다. FilterContainer 는 ml:auto 를 "마지막 자식"에게만 붙이는데,
        끄면 그 자리를 내부 null(접기 아이콘)이 차지해 내 액션 묶음이 가운데 남는다.
        기본값이면 아래 액션 Box 가 마지막 자식이 되어 오른쪽으로 밀리고 접기 아이콘이 옆에 붙는다.
      */}
      <FilterContainer>
        {/* 차원 전환 - 품목 ↔ 거래처. DP 워크벤치와 같은 아이콘·툴팁 */}
        <Box>
          <Tooltip title={t("dimension")}>
            <IconButton size="small" onClick={() => setOrderByItem(prev => !prev)}>
              <Transform />
            </IconButton>
          </Tooltip>
        </Box>
        {/* VersionAutocomplete 가 자체로 mr:1 을 갖는다 - 여기서 또 주면 두 배가 된다 */}
        <DpVersionSelect
          selectedVersion={setSelectVersion}
          reduxVersion={reduxViewState?.selectVersion ?? null}
          reload={vListReload}
          setReload={setVListReload}
          status={["SCENARIO", "RELEASED"]}
        />
        {/* 계층 트리와 코드 목록(깔때기)을 같은 축끼리 붙여 둔다 */}
        <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
          <ItemHrchyTreeFilter
            viewName={viewName}
            onChangeFilter={setFilterValue}
            defaultValue={itemHrchyDefault}
            enableSelectParent
            enableMultiSelect
            treeload={itemTreeload}
            styles={{ width: 240 }}
          />
          <IconButton
            size="small"
            color={size(itemFilterList) > 0 ? muicolor.INFO : muicolor.DEFAULT}
            onClick={() => { setModelCode(item.name); setFilterModalOpen(true); }}
          >
            <Tooltip title={t("itemFilter")}><FilterAlt /></Tooltip>
          </IconButton>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
          <CustomerHrchyTreeFilter
            viewName={viewName}
            onChangeFilter={setFilterValue}
            defaultValue={custHrchyDefault}
            enableSelectParent
            enableMultiSelect
            treeload={treeload}
            styles={{ width: 240 }}
          />
          <IconButton
            size="small"
            color={size(customerFilterList) > 0 ? muicolor.INFO : muicolor.DEFAULT}
            onClick={() => { setModelCode(customer.name); setFilterModalOpen(true); }}
          >
            <Tooltip title={t("customerFilter")}><FilterAlt /></Tooltip>
          </IconButton>
        </Box>

        <TextField
          select size="small" label={t("dnScope")} value={scope}
          onChange={e => setScope(e.target.value)}
          disabled={!isNettingMode} sx={{ minWidth: 140, mr: 1 }}
        >
          <MenuItem value="ALL">{t("dnScopeAll")}</MenuItem>
          <MenuItem value="MONTH">{t("dnScopeMonth")}</MenuItem>
          <MenuItem value="BUCKET">{t("dnScopeBucket")}</MenuItem>
        </TextField>

        <TextField
          select size="small" label={t("dnMethod")} value={method}
          onChange={e => setMethod(e.target.value)}
          disabled={!isNettingMode || methodDisabled} sx={{ minWidth: 140, mr: 1 }}
        >
          <MenuItem value="EARLIEST">{t("dnMethodEarliest")}</MenuItem>
          <MenuItem value="DUE">{t("dnMethodDue")}</MenuItem>
          <MenuItem value="BACKWARD">{t("dnMethodBackward")}</MenuItem>
        </TextField>

        {/* 조회 조건을 되돌린다 - 조건 끝자리에 둔다 */}
        <ResetButton onClick={handleReset} />

        {/* 필터 → 실행 순서. 조건을 먼저 좁히고 돌리는 흐름이라 왼쪽이 필터다 */}
        {/* 아이콘끼리는 붙인다 - IconButton 이 자체 패딩을 갖고 있어 gap 을 주면 과해진다 */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Button
            sx={{ mr: 1 }}
            variant="contained" size="small" startIcon={<PlayArrowIcon />}
            disabled={!enabled || !isNettingMode || running || isNil(selectVersion?.id)}
            onClick={runNetting}
          >
            {t("dnExecute")}
          </Button>
          {/* 노트 - 워크벤치와 같이 열고 닫기만 한다. 대상은 패널 안에서 셀 클릭으로 바뀐다 */}
          <Tooltip title={t("notes")}>
            <IconButton size="small" color={muicolor.INFO} onClick={toggleNotePanel}>
              <EditNote />
            </IconButton>
          </Tooltip>
          <FilterButton
            label="advancedFilter"
            advancedFilterColor={advancedFilterColor}
            onClick={() => setOpenFilter(prev => !prev)}
          />
        </Box>
      </FilterContainer>

      {(!enabled || !isNettingMode) && (
        <Typography variant="caption" sx={{ px: 2, py: 0.5, color: "text.secondary" }}>
          {t(!enabled ? "dnDisabledNotice" : "dnLegacyNotice")}
        </Typography>
      )}

      {/* 부모가 flex row 여야 ResizableFilterPanel 의 드래그 손잡이가 제자리에 선다 */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "row", px: 1, pb: 1 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          {/*
            * 가운데 오버레이 박스는 이 앱에서 로딩 표시다 - 다른 그리드가 전부 그렇게 쓴다.
            * 빈 결과를 여기에 띄우면 "불러오는 중" 으로 읽히므로 넣지 않는다. 결과가 없다는
            * 안내는 위의 실행 이력 줄이 한다("네팅을 실행한 적 없습니다").
            */}
          <Box className="ag-theme-balham" sx={{ flex: 1, minHeight: 0 }}>
            <AgGridReact
              ref={gridRef}
              {...defaultGridMemo}
              defaultColDef={readOnlyColDef}
              rowData={rows}
              columnDefs={gridColumnDefs}
              overlayLoadingTemplate={`<span class="ag-overlay-loading-center">${t("MSG_Loading")}</span>`}
              /*
               * 노트는 셀 단위다 - 버켓 칸을 눌러야 대상이 정해진다.
               * 조합의 2~5행은 품목 · 거래처 칸이 비어 있으므로 comboKey 로 되짚는다.
               */
              onCellClicked={p => {
                // 버켓 칸일 때만 갈아끼운다. 고정 컬럼(품목 · 거래처 · 지표 · 총계)을 눌렀다고
                // 선택을 지우면, 패널을 열어 둔 채 표를 훑기만 해도 대상이 사라진다.
                const target = cellTargetOf(p);
                if (target) setSelectedCell(target);
              }}
              suppressAggFuncInHeader
            />
          </Box>
          {/*
            * 실행 이력과 페이저를 한 줄에 둔다.
            *
            * 실행 이력이 툴바 바로 밑에 있었을 때는 왼쪽만 한 줄이 늘어 오른쪽 RUN NETTING 이
            * 혼자 위로 뜬 것처럼 보였다. 여기로 내리면 표의 상태를 표 옆에서 읽게 되고,
            * 페이저 행의 빈 왼쪽을 쓰므로 세로도 안 늘어난다.
            *
            * 양옆 균형추(flex:1, basis 0)로 페이저는 화면 가운데를 지킨다. 왼쪽 칸이 길어져도
            * 페이저가 밀리지 않도록 minWidth:0 + 말줄임을 건다.
            *
            * 두 칸 다 mb:1 이어야 세로 중심이 맞는다 - PaginationContainer 가 자기 Box 에
            * mb:1 을 갖고 있어서, 왼쪽에 없으면 글자가 버튼보다 반 칸 위로 뜬다.
            *
            * 실행 이력은 없어도 칸을 남긴다("안 돌린 것"과 "결과가 빈 것"은 다르다).
            */}
          <Box sx={{ pt: 1, display: "flex", alignItems: "center" }}>
            <Box sx={{ flex: 1, minWidth: 0, mb: 1 }}>
              {!isNil(selectVersion?.id) && (
                <Typography
                  component="div"
                  variant="caption"
                  sx={{ color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {isEmpty(executedOption) ? t("dnNeverExecuted") : (
                    <>
                      {`${t("dnLastExecuted")}: `}
                      <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                        {formatExecutedTs(executedOption.EXECUTED_TS)}
                      </Box>
                      {`  ·  ${t("dnScope")}: ${executedOption.SCOPE ?? "-"}  ·  ${t("dnMethod")}: ${executedOption.METHOD ?? "-"}`}
                    </>
                  )}
                </Typography>
              )}
            </Box>
            <PaginationContainer
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              pageSizes={PAGE_SIZES}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
            />
            {/* 오른쪽 균형추 - 이게 없으면 페이저가 왼쪽 칸만큼 오른쪽으로 밀린다 */}
            <Box sx={{ flex: 1, minWidth: 0 }} />
          </Box>
        </Box>

        {/* 노트 패널 - SidePanel 은 flex:none·height:100% 라 그리드와 같은 행에 서야 한다 */}
        {noteOpen && (
          <Box sx={{ display: "flex", ml: 1, minHeight: 0 }}>
            <DemandNettingNoteModal
              open
              close={toggleNotePanel}
              target={selectedCell}
              version={selectVersion}
              onSaved={(key, exists) => setNotedKeys(prev => {
                const next = new Set(prev);
                if (exists) next.add(key); else next.delete(key);
                return next;
              })}
            />
          </Box>
        )}

        {openFilter && (
          <ResizableFilterPanel>
            <AdvancedFilter
              columnDefs={filterColumnDefs}
              advancedFilters={advancedFilters}
              onApply={setAdvancedFilters}
              onClose={() => setOpenFilter(false)}
            />
          </ResizableFilterPanel>
        )}
      </Box>

      <DimensAttrModal
        open={dimenModalOpen}
        close={() => setDimenModalOpen(false)}
        viewName={viewName}
        dimensionAttrData={dimensionAttrData}
        setDimensionAttrData={setDimensionAttrData}
        attrConfig={attrConfig}
        selectLv={{
          isCust: dimenAttrTarget === "customer",
          // 말단(품목/거래처) 레벨로 연다 - 이 화면은 계층 레벨로 접지 않는다
          selectLvCd: dimenAttrTarget === "customer" ? customer.cd : item.cd,
          selectGrpCd: dimenAttrTarget === "customer" ? custHrchyGrp : itemHrchyGrp
        }}
        attrTarget={dimenAttrTarget}
      />

      <DPFilterModal
        open={filterModalOpen}
        close={() => setFilterModalOpen(false)}
        displayModButtons
        modelCode={modelCode}
        modalResultData={setFilterData}
        filterTypes={{ Customer: custFilterType, Item: itemFilterType }}
        filteredList={{ Customer: customerFilterList, Item: itemFilterList }}
      />

      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        content={snackInfo.content}
        severity={snackInfo.severity}
        color={snackBarMessageColor}
      />
    </Box>
  );
};

/** 응답에 나온 버켓을 정렬해 열 목록으로. 버전 설정이 아니라 데이터에서 뽑는다(혼합 버켓 대응). */
const collectBuckets = cells => [...new Set(cells.map(c => c.baseDate))].sort();

/**
 * 칸 목록을 품목 x 거래처마다 5행으로 접는다.
 *
 * 서버는 (품목, 거래처, 버켓) 단위 칸을 주고, 화면은 버켓을 열로 편다.
 */
/**
 * @param dimTabs 차원 설정에서 고른 탭 { item, customer } — 각각 { configObj, attr, ... }
 */
/** 버켓 컬럼의 field 는 항상 yyyy-MM-dd 다. 고정 컬럼(품목 · 지표 · 총계 …)과 이걸로 가른다. */
const BUCKET_FIELD = /^\d{4}-\d{2}-\d{2}$/;

/**
 * 그리드 셀 → 노트 대상. 버켓 칸이 아니면 null.
 *
 * buckets 상태와 대조하지 않는다 — 컬럼과 상태가 어긋나면 클릭이 조용히 무시되고,
 * 사용자는 칸을 눌렀는데도 "칸을 고르세요" 만 본다.
 */
const cellTargetOf = p => {
  const baseDate = p?.colDef?.field;
  if (!p?.data?.comboKey || !baseDate || !BUCKET_FIELD.test(baseDate)) return null;
  const [itemCd, customerCd] = p.data.comboKey.split("\u0000");
  return { itemCd, customerCd, baseDate, measureCd: p.data.rowKey, measureLabel: p.data.rowLabel };
};

const buildRows = (cells, dimTabs, notedKeys) => {
  const itemTab = dimTabs?.item;
  const custTab = dimTabs?.customer;
  const byKey = new Map();
  cells.forEach(cell => {
    const key = `${cell.itemCd}\u0000${cell.customerCd}`;
    if (!byKey.has(key)) {
      byKey.set(key, {
        itemCd: cell.itemCd, itemName: cell.itemName,
        customerCd: cell.customerCd, customerName: cell.customerName,
        itemAttrs: cell.itemAttrs, customerAttrs: cell.customerAttrs,
        itemHrchy: cell.itemHrchy, customerHrchy: cell.customerHrchy,
        invClassCd: cell.invClassCd,
        cells: {}
      });
    }
    byKey.get(key).cells[cell.baseDate] = cell;
  });

  const rows = [];
  byKey.forEach(group => {
    ROW_DEFS.forEach((def, idx) => {
      /*
       * 품목 · 거래처는 조합의 첫 행에만 찍는다. 5행이 전부 같은 이름을 반복하면 어디서
       * 조합이 바뀌는지가 안 읽힌다 (DP 워크벤치와 같은 모양).
       *
       * ag-grid 의 세로 병합(enableCellSpan)을 안 쓰는 이유 - CellSpanModule 이 FP 스코프
       * (factory-plan/common/grid.js)에만 등록돼 있고, 앱 전역 등록은 2026-08-11 에 하지
       * 않기로 한 결정이다. 데이터에서 비우면 모듈이 필요 없다.
       */
      const first = idx === 0;
      /*
       * 속성은 위치 필드로 편다 - 서버는 attr01..10 을 통째로 주고, 고른 순서가 컬럼 순서다.
       * 이름도 조합의 첫 행에만 찍는다(품목 · 거래처와 같은 규칙).
       */
      const dimFields = {};
      const fill = (scope, tab) => {
        const isItem = scope === "item";
        (tab?.configObj ?? []).forEach(level => {
          const src = isItem ? group.itemHrchy : group.customerHrchy;
          dimFields[`${isItem ? "item" : "cust"}Lv_${level.cd}`] = first ? (src?.[level.cd] ?? "") : "";
        });
        (tab?.attr ?? []).forEach((attrCd, i) => {
          const src = isItem ? group.itemAttrs : group.customerAttrs;
          dimFields[`${isItem ? "itemAttr" : "custAttr"}${i}`] = first ? (src?.[attrCd] ?? "") : "";
        });
      };
      fill("item", itemTab);
      fill("customer", custTab);
      dimFields.itemName = first ? (group.itemName ?? "") : "";
      dimFields.customerName = first ? (group.customerName ?? "") : "";
      dimFields.invClassCd = first ? (group.invClassCd ?? "") : "";
      /*
       * 조합 식별자. 화면상 2~5행은 품목 · 거래처 칸이 비어 있어서, 노트를 열 때 그 행에서
       * 조합을 되짚으려면 보이지 않는 키가 따로 필요하다. NUL 은 코드에 못 들어가는 문자다.
       */
      dimFields.comboKey = `${group.itemCd ?? ""}\u0000${group.customerCd ?? ""}`;
      /*
       * 이 행(지표)에서 노트가 달린 버켓들. 셀 단위 표식에 쓴다.
       * 키를 셀마다 만들어 대조하는 것보다 행마다 한 번 접는 편이 싸다.
       */
      const noteCells = new Set();
      Object.keys(group.cells).forEach(bucket => {
        if (notedKeys.has(buildNoteKey(group.customerCd, group.itemCd, bucket, def.key))) {
          noteCells.add(bucket);
        }
      });
      dimFields.noteCells = noteCells;
      const row = {
        ...dimFields,
        itemCd: first ? group.itemCd : "",
        itemName: first ? group.itemName : "",
        customerCd: first ? group.customerCd : "",
        customerName: first ? group.customerName : "",
        rowKey: def.key,
        rowLabel: def.label,
        groupLevel: def.groupLevel === true,
        replyRow: def.reply === true,
        // 숫자가 아닌 행(지연 도착 예정). 수량 서식·합계에서 빼야 한다.
        textRow: def.text === true,
        // 잔액 성격의 행. 버켓을 더하면 같은 물량을 여러 번 세게 된다.
        noTotalRow: def.noTotal === true
      };
      /*
       * 회신 없음을 표시하려면 "값이 null" 만으로는 부족하다 — 조회 기간에 셀 자체가 없는
       * 버켓과 구분이 안 된다. 회신 행에서 셀은 있는데 값이 없는 버켓만 따로 모은다.
       */
      const noReplyCells = new Set();
      const tipCells = {};
      /*
       * 그룹이 지평 전체인 값은 버켓 칸에 놓지 않는다 — 어느 칸에 놓아도 그 버켓의 값으로
       * 읽히기 때문이다. Total 열로 보낸다(groupTotal).
       */
      const wholeRange = typeof def.wholeRange === "function"
        && Object.values(group.cells).some(cell => def.wholeRange(cell));
      // 버켓을 가로질러 구하는 행(지연 잔량)은 칸 단위 pick 으로 못 만든다. 한 번에 계산한다.
      const series = typeof def.series === "function" ? def.series(group.cells) : null;
      if (series) Object.entries(series).forEach(([bucket, value]) => { row[bucket] = value; });
      Object.entries(group.cells).forEach(([bucket, cell]) => {
        if (series) return;
        const value = def.pick(cell);
        if (wholeRange) {
          if (!isNil(value)) row.groupTotal = (row.groupTotal ?? 0) + Number(value);
        } else {
          row[bucket] = value;
        }
        if (def.reply && isNil(value)) noReplyCells.add(bucket);
        if (def.tip) {
          const tip = def.tip(cell);
          if (!isNil(tip)) tipCells[bucket] = tip;
        }
      });
      row.noReplyCells = noReplyCells;
      row.tipCells = tipCells;
      rows.push(row);
    });
  });
  return rows;
};

/**
 * @param orderByItem true 면 품목 → 거래처, false 면 거래처 → 품목.
 *   서버 정렬(comboBase 의 ORDER BY)과 <b>같은 방향</b>이어야 한다. 조합 단위로 페이징하므로
 *   정렬이 곧 페이지 내용이다 — 화면만 뒤집으면 페이지 안에서만 뒤섞인 것처럼 보인다.
 */
/**
 * 차원 설정에서 고른 것을 컬럼으로 편다.
 *
 * hasLevel 모드의 저장 구조는 {@code dimensionAttrData[item|customer][grpCd]} 이고
 * 한 탭의 값은 {@code { configObj, attr, hasCode, hasAbcXyz }} 다.
 *   - configObj : 계층 레벨 설정 객체들. seqNum 순으로 컬럼이 된다
 *   - attr      : 고른 사용자 속성. 고른 순서가 컬럼 순서다
 *   - hasCode   : 코드 컬럼(품목코드/거래처코드)
 *   - hasAbcXyz : ABC-XYZ 등급 (품목에만 있다)
 *
 * 값은 서버가 통째로 준 맵에서 꺼낸다 — 계층은 레벨코드(ITEM_LV2 …), 속성은 attr01 … 키다.
 */
const dimensionColumnDefs = (scope, tabData, attrConfig, t) => {
  const cols = [];
  const isItem = scope === "item";
  const prefix = isItem ? "item" : "cust";

  // 계층 레벨 - 상위부터
  const levels = [...(tabData?.configObj ?? [])].sort((a, b) => (a.seqNum ?? 0) - (b.seqNum ?? 0));
  levels.forEach(level => {
    cols.push({
      field: `${prefix}Lv_${level.cd}`,
      headerName: level.name ?? level.cd,
      pinned: "left", width: 130, cellClass: "stringType"
    });
  });

  // 코드 - 켜져 있을 때만. 이 화면은 코드가 기본 컬럼이라 중복을 피한다
  if (tabData?.hasCode) {
    cols.push({
      field: isItem ? "itemName" : "customerName",
      headerName: t(isItem ? "itemName" : "customerName"),
      pinned: "left", width: 160, cellClass: "stringType"
    });
  }

  if (isItem && tabData?.hasAbcXyz) {
    cols.push({ field: "invClassCd", headerName: t("invClassCd"), pinned: "left", width: 110, cellClass: "stringType" });
  }

  (tabData?.attr ?? []).forEach((attrCd, idx) => {
    cols.push({
      field: `${isItem ? "itemAttr" : "custAttr"}${idx}`,
      headerName: attrConfig?.[scope]?.[attrCd] ?? attrCd,
      pinned: "left", width: 120, cellClass: "stringType"
    });
  });
  return cols;
};

const buildColumnDefs = (t, buckets, orderByItem, dimTabs, attrConfig, openDimen) => {
  // 헤더의 톱니 아이콘 - 클릭하면 차원 설정 모달이 그 축으로 열린다
  const dimenHeader = target => ({
    headerComponent: CustomHeaderMenuIcon,
    headerComponentParams: { enableMenu: false, isSorting: false, isClicked: () => openDimen(target) }
  });
  const itemBlock = [
    { field: "itemCd", headerName: t("item"), pinned: "left", width: 140, ...dimenHeader("item") },
    ...dimensionColumnDefs("item", dimTabs?.item, attrConfig, t)
  ];
  const custBlock = [
    { field: "customerCd", headerName: t("customer"), pinned: "left", width: 140, ...dimenHeader("customer") },
    ...dimensionColumnDefs("customer", dimTabs?.customer, attrConfig, t)
  ];
  return [
    ...(orderByItem ? [...itemBlock, ...custBlock] : [...custBlock, ...itemBlock]),
    {
      field: "rowLabel", headerName: t("measure"), pinned: "left", width: 170,
      valueFormatter: p => t(p.value),
      cellStyle: { fontWeight: 500 }
    },
    /*
     * 합계. 버켓 값을 그 자리에서 더한다 - 서버가 따로 내려주지 않는다.
     *
     * 예측 초과 SO 행은 그룹의 첫 버켓에만 값이 있어(서버가 그렇게 채운다) 더해도 그룹 합계
     * 그대로다. 나머지 네 행은 기간 전체의 합이 된다.
     * 차감 범위가 [전 구간]이면 그 행은 버켓 칸이 아예 없고 groupTotal 로만 온다.
     */
    {
      field: "total", headerName: t("total"), pinned: "left", width: 110,
      type: "numericColumn",
      valueGetter: p => {
        if (!p.data) return null;
        // 날짜 행과 잔액 행은 더할 수 있는 값이 아니다.
        if (p.data.textRow || p.data.noTotalRow) return null;
        // 지평 전체가 한 그룹인 값은 버켓 칸이 없다. 여기가 그 값의 자리다.
        if (!isNil(p.data.groupTotal)) return p.data.groupTotal;
        let sum = 0;
        let seen = false;
        buckets.forEach(bucket => {
          const v = p.data[bucket];
          if (!isNil(v) && v !== "" && !isNaN(v)) { sum += Number(v); seen = true; }
        });
        return seen ? sum : null;
      },
      valueFormatter: p => fmtQty(p.value),
      cellStyle: { fontWeight: 600 }
    },
    ...buckets.map(bucket => ({
      field: bucket,
      headerName: bucket,
      type: "numericColumn",
      width: 110,
      // 회신이 안 온 칸은 빈칸이 아니라 "-" 다. 빈칸으로 두면 0 으로 읽힌다.
      // 날짜 행은 수량 서식을 태우면 안 된다 - 그대로 찍는다.
      valueFormatter: p => {
        if (p.data?.textRow) return isNil(p.value) ? "" : String(p.value);
        return p.data?.noReplyCells?.has(bucket) ? "-" : fmtQty(p.value);
      },
      // 노트 표식 — 워크벤치와 같은 초록 왼쪽 테두리. 노트가 셀 단위라 그 칸에만 찍는다
      cellStyle: p => (p.data?.noteCells?.has(bucket) ? { borderLeft: "3px solid #4caf50" } : undefined),
      tooltipValueGetter: p => {
        // 그룹 단위 값은 그룹의 첫 버켓에만 있다. 빈 칸을 0 으로 오해하지 않도록 안내를 붙인다.
        if (p.data?.groupLevel) return t("dnExcessTooltip");
        if (p.data?.noReplyCells?.has(bucket)) return t("dnRtfNoReply");
        const tip = p.data?.tipCells?.[bucket];
        if (isNil(tip)) return undefined;
        const lines = [];
        // 지연분이 언제 들어오는지. 양만으로는 영업이 판단할 수 없다.
        if (!isNil(tip.date)) lines.push(t("dnRtfDelayTooltip", { date: tip.date }));
        // 어느 계획이 낸 답인지. 숨기면 "왜 MP 화면과 다르냐" 가 반드시 나온다.
        if (!isNil(tip.source)) lines.push(t("dnRtfSourceTooltip", { source: t(`dnRtfSource${tip.source}`) }));
        return lines.length ? lines.join("\n") : undefined;
      }
    })),
  ];
};

export default withTranslation()(DemandNetting);
